import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiCalendar,
  FiClock,
  FiDollarSign,
  FiDroplet,
  FiMail,
  FiMapPin,
  FiPhone,
  FiScissors,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Loader from "../components/common/Loader";
import Button from "../components/common/Button";
import { BOOKING_STATUS, SERVICE_MODE } from "../utils/constants";
import { formatDate, formatTime } from "../utils/formatDate";
import formatCurrency from "../utils/formatCurrency";
import * as userService from "../services/userService";
import * as bookingService from "../services/bookingService";

const statusClassMap = {
  [BOOKING_STATUS.CONFIRMED]: "status-chip status-chip-confirmed",
  [BOOKING_STATUS.CANCELED]: "status-chip status-chip-canceled",
  [BOOKING_STATUS.COMPLETED]: "status-chip status-chip-completed",
};

const safeCallGetMyProfile = async () => {
  if (typeof userService.getMyProfile === "function") {
    return userService.getMyProfile();
  }

  if (typeof userService.getProfile === "function") {
    return userService.getProfile();
  }

  if (typeof userService.getMe === "function") {
    return userService.getMe();
  }

  throw new Error("Profile service method was not found in userService.");
};

const safeCallGetUserBookings = async (userId) => {
  if (typeof bookingService.getBookingsByUserId === "function") {
    return bookingService.getBookingsByUserId(userId);
  }

  if (typeof bookingService.getBookingsByUser === "function") {
    return bookingService.getBookingsByUser(userId);
  }

  if (typeof bookingService.getUserBookings === "function") {
    return bookingService.getUserBookings(userId);
  }

  throw new Error("Booking service method was not found in bookingService.");
};

const normalizeBookingItems = (booking) => {
  if (Array.isArray(booking?.bookingItems) && booking.bookingItems.length > 0) {
    return booking.bookingItems;
  }

  if (booking?.service) {
    return [
      {
        service: booking.service,
        hennaType: booking.hennaType || booking.service.hennaType || "",
        numOfAdults: booking.numOfAdults ?? 0,
        numOfChildren: booking.numOfChildren ?? 0,
        partySize: booking.partySize ?? 0,
        bookingDurationMinutes: booking.bookingDurationMinutes ?? 0,
        bookingPrice: booking.bookingPrice ?? 0,
        sortOrder: 0,
      },
    ];
  }

  return [];
};

const buildOnsiteAddressText = (onsiteAddress) => {
  if (!onsiteAddress) return "";

  return [
    onsiteAddress.line1,
    onsiteAddress.line2,
    onsiteAddress.city,
    onsiteAddress.state,
    onsiteAddress.zip,
  ]
    .filter(Boolean)
    .join(", ");
};

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeFilter, setActiveFilter] = useState("ALL");

  const fullName = useMemo(() => {
    if (!profile) return "";
    return `${profile.firstname || ""} ${profile.lastname || ""}`.trim();
  }, [profile]);

  const sortedBookings = useMemo(() => {
    return [...bookings].sort((a, b) => {
      const aDateTime = new Date(
        `${a?.bookingDate || "1970-01-01"}T${a?.startTime || "00:00"}`
      ).getTime();

      const bDateTime = new Date(
        `${b?.bookingDate || "1970-01-01"}T${b?.startTime || "00:00"}`
      ).getTime();

      return bDateTime - aDateTime;
    });
  }, [bookings]);

  const filteredBookings = useMemo(() => {
    if (activeFilter === "ALL") return sortedBookings;
    return sortedBookings.filter((booking) => booking.status === activeFilter);
  }, [sortedBookings, activeFilter]);

  useEffect(() => {
    const loadProfileAndBookings = async () => {
      try {
        setLoading(true);
        setError("");

        const profileData = await safeCallGetMyProfile();

        if (profileData?.statusCode && profileData.statusCode !== 200) {
          throw new Error(profileData.message || "Unable to load profile.");
        }

        const user = profileData?.user || null;
        setProfile(user);

        if (user?.id) {
          setBookingsLoading(true);

          try {
            const bookingsData = await safeCallGetUserBookings(user.id);

            if (bookingsData?.statusCode && bookingsData.statusCode !== 200) {
              throw new Error(bookingsData.message || "Unable to load bookings.");
            }

            setBookings(bookingsData?.bookingList || []);
          } finally {
            setBookingsLoading(false);
          }
        }
      } catch (err) {
        setError(err?.message || "We could not load your profile.");
      } finally {
        setLoading(false);
      }
    };

    loadProfileAndBookings();
  }, []);

  if (loading) {
    return <Loader fullScreen text="Loading your account..." />;
  }

  return (
    <div className="page-section">
      <PageContainer>
        <SectionTitle
          eyebrow="My Account"
          title="Your profile"
          subtitle="View your account details, revisit your bookings, and keep everything you need in one place."
          className="mb-8"
        />

        {error ? (
          <div className="content-card-lg p-6">
            <p className="text-sm font-medium text-red-600">{error}</p>
          </div>
        ) : null}

        {!error && (
          <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)]">
            <aside className="content-card-lg p-6">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-soft-cream text-henna">
                <FiUser size={28} />
              </div>

              <h1 className="mt-4 text-2xl font-semibold text-henna">
                {fullName || "Your Profile"}
              </h1>

              <p className="mt-1 text-sm text-muted-warm">
                {profile?.role || "CLIENT"}
              </p>

              <div className="mt-6 space-y-4">
                <div className="flex items-start gap-3">
                  <FiMail className="mt-0.5 text-henna" />
                  <div>
                    <p className="text-sm font-medium text-[#3b2f2f]">Email</p>
                    <p className="text-sm text-muted-warm">{profile?.email || "—"}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FiPhone className="mt-0.5 text-henna" />
                  <div>
                    <p className="text-sm font-medium text-[#3b2f2f]">Phone</p>
                    <p className="text-sm text-muted-warm">
                      {profile?.phoneNumber || "Not provided"}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <FiUser className="mt-0.5 text-henna" />
                  <div>
                    <p className="text-sm font-medium text-[#3b2f2f]">
                      Email Verified
                    </p>
                    <p className="text-sm text-muted-warm">
                      {profile?.emailVerified ? "Yes" : "No"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 rounded-2xl bg-soft-cream p-4">
                <p className="text-sm font-semibold text-henna">Quick tools</p>
                <div className="mt-4 flex flex-col gap-3">
                  <Link to="/booking-confirmation">
                    <Button variant="secondary" fullWidth>
                      Find a Booking
                    </Button>
                  </Link>

                  <Link to="/booking-cancel">
                    <Button variant="outline" fullWidth>
                      Cancel with Code
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="mt-6 rounded-2xl bg-soft-cream p-4">
                <p className="text-sm font-semibold text-henna">Helpful reminder</p>
                <p className="mt-2 text-sm leading-6 text-muted-warm">
                  Studio means you will go to the artist. Mobile means the artist
                  will travel to your location and you will take care of the
                  artist travel expenses. Confirmed bookings can only be
                  cancelled 48 hours prior to your booked appointment.
                </p>
              </div>
            </aside>

            <section className="min-w-0 space-y-6">
              <div className="content-card-lg p-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-henna">
                      Your bookings
                    </h2>
                    <p className="mt-1 text-sm text-muted-warm">
                      Review your upcoming and past appointments in one place.
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {[
                      "ALL",
                      BOOKING_STATUS.CONFIRMED,
                      BOOKING_STATUS.CANCELED,
                      BOOKING_STATUS.COMPLETED,
                    ].map((filter) => (
                      <Button
                        key={filter}
                        variant={activeFilter === filter ? "primary" : "secondary"}
                        size="sm"
                        onClick={() => setActiveFilter(filter)}
                      >
                        {filter}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="content-card-lg p-6">
                {bookingsLoading ? (
                  <Loader text="Loading your bookings..." />
                ) : filteredBookings.length === 0 ? (
                  <div className="empty-state">
                    <p className="text-base font-medium text-[#3b2f2f]">
                      No bookings found.
                    </p>
                    <p className="mt-2 text-sm text-muted-warm">
                      Once you make a booking, it will appear here.
                    </p>
                  </div>
                ) : (
                  <div className="max-h-[600px] space-y-4 overflow-y-auto pr-2">
                    {filteredBookings.map((booking) => {
                      const items = normalizeBookingItems(booking);
                      const bookingPrice = booking?.bookingPrice;
                      const statusClass =
                        statusClassMap[booking?.status] || "status-chip";
                      const modeText =
                        booking?.serviceMode === SERVICE_MODE.MOBILE
                          ? "Mobile"
                          : "Studio";

                      return (
                        <div
                          key={booking.id}
                          className="rounded-2xl border border-goldlight bg-white p-5 shadow-soft"
                        >
                          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-3">
                                <h3 className="text-lg font-semibold text-henna">
                                  {items.length > 1
                                    ? `${items.length} Services Booked`
                                    : items[0]?.service?.serviceName ||
                                      "Selected Service"}
                                </h3>
                                <span className={statusClass}>
                                  {booking?.status || "UNKNOWN"}
                                </span>
                              </div>

                              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                <div className="flex items-start gap-3">
                                  <FiCalendar className="mt-0.5 text-henna" />
                                  <div>
                                    <p className="text-sm font-medium text-[#3b2f2f]">
                                      Date
                                    </p>
                                    <p className="text-sm text-muted-warm">
                                      {booking?.bookingDate
                                        ? formatDate(booking.bookingDate)
                                        : "—"}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-start gap-3">
                                  <FiClock className="mt-0.5 text-henna" />
                                  <div>
                                    <p className="text-sm font-medium text-[#3b2f2f]">
                                      Time
                                    </p>
                                    <p className="text-sm text-muted-warm">
                                      {booking?.startTime
                                        ? formatTime(booking.startTime)
                                        : "—"}{" "}
                                      -{" "}
                                      {booking?.endTime
                                        ? formatTime(booking.endTime)
                                        : "—"}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-start gap-3">
                                  <FiUsers className="mt-0.5 text-henna" />
                                  <div>
                                    <p className="text-sm font-medium text-[#3b2f2f]">
                                      Party Size
                                    </p>
                                    <p className="text-sm text-muted-warm">
                                      {booking?.partySize ?? "—"}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-start gap-3">
                                  <FiMapPin className="mt-0.5 text-henna" />
                                  <div>
                                    <p className="text-sm font-medium text-[#3b2f2f]">
                                      Appointment Type
                                    </p>
                                    <p className="text-sm text-muted-warm">
                                      {modeText}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-start gap-3">
                                  <FiDollarSign className="mt-0.5 text-henna" />
                                  <div>
                                    <p className="text-sm font-medium text-[#3b2f2f]">
                                      Total Price
                                    </p>
                                    <p className="text-sm text-muted-warm">
                                      {bookingPrice !== undefined &&
                                      bookingPrice !== null
                                        ? formatCurrency(bookingPrice)
                                        : "N/A"}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-start gap-3">
                                  <FiClock className="mt-0.5 text-henna" />
                                  <div>
                                    <p className="text-sm font-medium text-[#3b2f2f]">
                                      Total Duration
                                    </p>
                                    <p className="text-sm text-muted-warm">
                                      {booking?.bookingDurationMinutes !== undefined &&
                                      booking?.bookingDurationMinutes !== null
                                        ? `${booking.bookingDurationMinutes} minutes`
                                        : "—"}
                                    </p>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-5 rounded-2xl bg-soft-cream p-4">
                                <div className="flex items-center gap-2">
                                  <FiScissors className="text-henna" />
                                  <p className="text-sm font-semibold text-henna">
                                    Booked Services
                                  </p>
                                </div>

                                <div className="mt-4 space-y-3">
                                  {items.map((item, index) => (
                                    <div
                                      key={`${booking.id}-item-${index}`}
                                      className="rounded-xl bg-white p-4"
                                    >
                                      <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                          <p className="text-sm font-semibold text-[#3b2f2f]">
                                            {item?.service?.serviceName ||
                                              `Service ${index + 1}`}
                                          </p>
                                          <div className="mt-2 space-y-1 text-sm text-muted-warm">
                                            <p>
                                              Henna Type:{" "}
                                              {item?.hennaType ||
                                                item?.service?.hennaType ||
                                                "—"}
                                            </p>
                                            <p>
                                              Adults: {item?.numOfAdults ?? 0}
                                            </p>
                                            <p>
                                              Children: {item?.numOfChildren ?? 0}
                                            </p>
                                            <p>
                                              Party Size: {item?.partySize ?? 0}
                                            </p>
                                          </div>
                                        </div>

                                        <div className="text-right">
                                          <p className="text-sm font-medium text-[#3b2f2f]">
                                            {item?.bookingPrice !== undefined &&
                                            item?.bookingPrice !== null
                                              ? formatCurrency(item.bookingPrice)
                                              : "—"}
                                          </p>
                                          <p className="mt-1 text-xs text-muted-warm">
                                            {item?.bookingDurationMinutes !== undefined &&
                                            item?.bookingDurationMinutes !== null
                                              ? `${item.bookingDurationMinutes} minutes`
                                              : "—"}
                                          </p>
                                        </div>
                                      </div>

                                      {item?.hennaType === "Jaguar" ? (
                                        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                                          <div className="flex items-start gap-2">
                                            <FiDroplet className="mt-0.5 text-henna" />
                                            <p className="text-sm leading-6 text-muted-warm">
                                              Jaguar henna adds $25 and should be
                                              requested at least two weeks before
                                              the appointment.
                                            </p>
                                          </div>
                                        </div>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              </div>

                              {booking?.bookingConfirmationCode ? (
                                <div className="mt-4 rounded-xl bg-soft-cream p-3">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                                    Confirmation Code
                                  </p>
                                  <p className="mt-1 text-sm font-medium text-[#3b2f2f]">
                                    {booking.bookingConfirmationCode}
                                  </p>
                                </div>
                              ) : null}

                              {booking?.serviceMode === SERVICE_MODE.MOBILE &&
                              booking?.onsiteAddress ? (
                                <div className="mt-4">
                                  <p className="text-sm font-medium text-[#3b2f2f]">
                                    Service Address
                                  </p>
                                  <p className="mt-1 text-sm leading-6 text-muted-warm">
                                    {buildOnsiteAddressText(booking.onsiteAddress) || "—"}
                                  </p>
                                </div>
                              ) : null}

                              {booking?.locationNotes ? (
                                <div className="mt-4">
                                  <p className="text-sm font-medium text-[#3b2f2f]">
                                    Location Notes
                                  </p>
                                  <p className="mt-1 text-sm leading-6 text-muted-warm">
                                    {booking.locationNotes}
                                  </p>
                                </div>
                              ) : null}

                              {booking?.notes ? (
                                <div className="mt-4">
                                  <p className="text-sm font-medium text-[#3b2f2f]">
                                    Notes
                                  </p>
                                  <p className="mt-1 text-sm leading-6 text-muted-warm">
                                    {booking.notes}
                                  </p>
                                </div>
                              ) : null}
                            </div>

                            <div className="shrink-0 rounded-xl bg-soft-cream px-4 py-3 text-right">
                              <p className="text-xs font-semibold uppercase tracking-wide text-golddeep">
                                Booking Total
                              </p>
                              <p className="mt-1 text-lg font-semibold text-henna">
                                {bookingPrice !== undefined && bookingPrice !== null
                                  ? formatCurrency(bookingPrice)
                                  : "N/A"}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          </div>
        )}
      </PageContainer>
    </div>
  );
};

export default Profile;